import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  WEEK_DAYS_ORDER,
  type StudyDaysSelectorHandle,
  type StudyDaysSelectorProps,
} from './StudyDaysSelector.types';
import styles from './StudyDaysSelector.module.scss';

/**
 * Selector multi-opción de los días de la semana en que el alumno quiere
 * estudiar. Se usa en el slide de personalización del onboarding, justo antes
 * de "¿Cuándo prefieres estudiar?" (la hora). El valor se persiste en
 * `preferences.studyDays` y condiciona qué días se envía el recordatorio.
 *
 * Mínimo un día. Por defecto arranca con el día de la semana actual marcado
 * (mismo criterio que la hora, que toma la hora actual). Mantiene la selección
 * internamente y la expone vía ref imperativa para que el padre la persista.
 */
export const StudyDaysSelector = forwardRef<StudyDaysSelectorHandle, StudyDaysSelectorProps>(
  function StudyDaysSelector(
    { defaultDays, value, onChange, disabled = false, hideHelper = false, onValidityChange },
    ref
  ) {
    const { t } = useTranslation();
    const isControlled = value != null;

    // Estado interno solo en modo NO controlado (onboarding): lo que pase el
    // padre (días ya guardados) o, en su defecto, el día de hoy.
    const [internal, setInternal] = useState<Set<number>>(() => {
      const initial =
        defaultDays && defaultDays.length > 0 ? defaultDays : [new Date().getDay()];
      return new Set(initial);
    });

    const selected = useMemo(
      () => (isControlled ? new Set(value) : internal),
      [isControlled, value, internal]
    );

    const orderedSelected = useMemo(
      () => WEEK_DAYS_ORDER.filter((d) => selected.has(d)),
      [selected]
    );

    const lastNotifiedValidRef = useRef<boolean | null>(null);
    useEffect(() => {
      const isValid = orderedSelected.length > 0;
      if (lastNotifiedValidRef.current === isValid) return;
      lastNotifiedValidRef.current = isValid;
      onValidityChange?.(isValid);
    }, [orderedSelected, onValidityChange]);

    useImperativeHandle(
      ref,
      () => ({
        getSelected: () => orderedSelected,
        isValid: () => orderedSelected.length > 0,
      }),
      [orderedSelected]
    );

    const toggle = (day: number) => {
      const next = new Set(selected);
      if (next.has(day)) {
        // No permitir quedarse sin ningún día (mínimo 1).
        if (next.size === 1) return;
        next.delete(day);
      } else {
        next.add(day);
      }
      const ordered = WEEK_DAYS_ORDER.filter((d) => next.has(d));
      onChange?.(ordered);
      if (!isControlled) setInternal(next);
    };

    return (
      <div className={styles.selector}>
        <div
          className={styles.selector__list}
          role="group"
          aria-label={t('studyDaysSelector.ariaLabel')}
        >
          {WEEK_DAYS_ORDER.map((day) => {
            const isOn = selected.has(day);
            return (
              <button
                key={day}
                type="button"
                className={styles.selector__day}
                data-no-press-scale=""
                aria-pressed={isOn}
                disabled={disabled}
                onClick={() => toggle(day)}
              >
                {t(`studyDaysSelector.dayTwoLetter.${day}`)}
              </button>
            );
          })}
        </div>
        {!hideHelper && <p className={styles.helper}>{t('studyDaysSelector.helper')}</p>}
      </div>
    );
  }
);

StudyDaysSelector.displayName = 'StudyDaysSelector';
